import { db } from '../db/indexeddb'
import type { SyncQueueItem } from '../db/models'

/**
 * Sync Queue Management
 * Manages pending changes that need to be synced to cloud
 */

const MAX_RETRIES = 5

/**
 * Add change to sync queue
 */
export async function queueChange(
  action: 'create' | 'update' | 'delete',
  table: 'tasks' | 'settings' | 'projects' | 'tags',
  recordId: number,
  data: any
): Promise<void> {
  try {
    await db.syncQueue.add({
      action,
      table,
      recordId,
      data,
      synced: false,
      timestamp: new Date(),
      retryCount: 0,
    })

  } catch (error) {
    console.error('[SyncQueue] Failed to queue change:', error)
  }
}

/**
 * Get pending changes (excludes items that exceeded retry limit)
 */
export async function getPendingChanges(): Promise<SyncQueueItem[]> {
  return db.syncQueue
    .filter((item) => item.synced === false && (item.retryCount ?? 0) < MAX_RETRIES)
    .sortBy('timestamp')
}

/**
 * Mark change as synced
 */
export async function markAsSynced(queueItemId: number): Promise<void> {
  await db.syncQueue.update(queueItemId, { synced: true })
}

/**
 * Mark change as failed and increment retry count.
 * Items that exceed MAX_RETRIES are logged and will no longer be retried.
 */
export async function markAsFailed(queueItemId: number, error: string): Promise<void> {
  const item = await db.syncQueue.get(queueItemId)
  const retryCount = (item?.retryCount ?? 0) + 1

  await db.syncQueue.update(queueItemId, {
    error,
    synced: false,
    retryCount,
  })

  if (retryCount >= MAX_RETRIES) {
    console.error(`[SyncQueue] Item ${queueItemId} permanently failed after ${MAX_RETRIES} retries: ${error}`)
  }
}

/**
 * Clear synced queue items (cleanup)
 */
export async function clearSyncedItems(olderThanDays = 7): Promise<number> {
  try {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - olderThanDays)

    const deleted = await db.syncQueue
      .filter((item) => item.synced === true && item.timestamp < cutoffDate)
      .delete()

    return deleted
  } catch (error) {
    console.warn('[SyncQueue] clearSyncedItems failed:', error)
    return 0
  }
}

/**
 * Get sync queue statistics
 */
export async function getSyncQueueStats() {
  const allItems = await db.syncQueue.toArray()

  const pending = allItems.filter((item) => !item.synced && !item.error).length
  const synced = allItems.filter((item) => item.synced).length
  const failed = allItems.filter((item) => !item.synced && !!item.error).length

  return {
    pending,
    synced,
    failed,
    total: allItems.length,
  }
}

/**
 * Retry failed sync items (resets retry count)
 */
export async function retryFailedItems(): Promise<SyncQueueItem[]> {
  const failedItems = await db.syncQueue
    .filter((item) => !item.synced && !!item.error)
    .toArray()

  // Clear error and reset retry count
  await Promise.all(
    failedItems.map((item) =>
      db.syncQueue.update(item.id!, {
        error: undefined,
        synced: false,
        retryCount: 0,
      })
    )
  )

  return failedItems
}
